import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Waveform } from "ldrs/react";
import "ldrs/react/Waveform.css";
import {
  useMicMonitor,
  elevenLabsTTSStream, // changed import
  speechToText,
} from "./ElevenLabsInteractions";
import { sendToOpenAI } from "./OpenAiInteractions";

type CallState = "connecting" | "listening" | "thinking" | "talking";

const headerHeight = "h-16"; // 64px

const PitchFabriceVoiceView: React.FC = () => {
  const [callStarted, setCallStarted] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [callEnded, setCallEnded] = useState(false);
  const [dotIndex, setDotIndex] = useState(0);
  const [callState, setCallState] = useState<CallState>("connecting");
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [callSessionId, setCallSessionId] = useState(0);
  const [pendingStartSessionId, setPendingStartSessionId] = useState<
    number | null
  >(null);
  const callSessionIdRef = useRef(callSessionId); // Add ref for sync access
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  // --- Refs for TTS/STT ---
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ttsAbortController = useRef<AbortController | null>(null);

  // Debounce/guard for user speak interruptions
  const lastUserSpeakTime = useRef<number>(0);

  // Use micMonitor as before
  const micMonitor = useMicMonitor(handleUserSpeak); // revert to original usage
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const ttsPlayingRef = useRef(false);
  const listeningRef = useRef(false);
  const micStreamRef = useRef<MediaStream | null>(null);
  const recognitionRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);

  // --- Animation for connecting/listening dots ---
  useEffect(() => {
    if (
      callStarted &&
      !callEnded &&
      (callState === "connecting" ||
        callState === "listening" ||
        callState === "thinking")
    ) {
      const interval = setInterval(() => {
        setDotIndex((prev) => (prev + 1) % 3);
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDotIndex(0);
    }
  }, [callStarted, callEnded, callState]);

  // --- Stop recognition if mic is off or call ended ---
  useEffect(() => {
    if (!micOn || callEnded) {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.abort?.();
        } catch (err) {
          console.error("Error stopping recognition:", err);
        }
        recognitionRef.current = null;
      }
      listeningRef.current = false;
    }
  }, [micOn, callEnded]);

  // --- Microphone stream management ---
  useEffect(() => {
    if (callStarted && !callEnded && micOn) {
      if (!micStreamRef.current) {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            micStreamRef.current = stream;
            stream.getAudioTracks().forEach((track) => {
              track.enabled = micOn;
            });
          })
          .catch((err) => {
            console.error("Failed to get microphone stream:", err);
            micStreamRef.current = null;
          });
      } else {
        micStreamRef.current.getAudioTracks().forEach((track) => {
          track.enabled = micOn;
        });
      }
    } else {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => {
          track.stop();
          track.enabled = false; // Explicitly disable track
        });
        micStreamRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => {
          track.stop();
          track.enabled = false;
        });
        micStreamRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.abort?.();
        } catch (err) {
          console.error("Error stopping recognition on unmount:", err);
        }
        recognitionRef.current = null;
      }
    };
  }, [callStarted, callEnded, micOn]);

  // --- Main call flow logic ---
  const handleStartCall = () => {
    const newSessionId = callSessionIdRef.current + 1;
    setCallSessionId(newSessionId);
    callSessionIdRef.current = newSessionId;

    // Reset all refs and state for a fresh call
    listeningRef.current = false;
    ttsPlayingRef.current = false;
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== "closed") {
          audioContextRef.current.close();
        }
      } catch {}
      audioContextRef.current = null;
    }
    audioSourceRef.current = null;
    recognitionRef.current = null;

    setCallStarted(true);
    setMicOn(true);
    setCallEnded(false);
    setCallState("connecting");
    setThreadId(undefined);
    setPendingStartSessionId(newSessionId); // trigger effect to start conversation
  };

  // Start conversation only after state is updated and callStarted is true
  useEffect(() => {
    if (
      pendingStartSessionId !== null &&
      callStarted &&
      !callEnded &&
      callState === "connecting"
    ) {
      startConversation(pendingStartSessionId);
      setPendingStartSessionId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingStartSessionId, callStarted, callEnded, callState]);

  // --- Conversation logic ---
  async function startConversation(sessionId: number) {
    try {
      setCallState("connecting");
      // Do not send threadId for initial "Hi"
      const aiResult = await sendToOpenAI("Hi", undefined, undefined);
      if (callSessionIdRef.current !== sessionId || callEnded) return;
      if (aiResult.threadId) setThreadId(aiResult.threadId);
      await playTTS(aiResult.response, sessionId);
      if (callSessionIdRef.current !== sessionId || callEnded) return;
      setCallState("listening");
      await listenAndRespond(sessionId);
    } catch (e) {
      if (callSessionIdRef.current !== sessionId || callEnded) return;
      console.error("Error starting conversation:", e);
      setCallState("connecting");
    }
  }

  async function listenAndRespond(sessionId?: number) {
    const sid = sessionId ?? callSessionIdRef.current;
    if (
      !micOn ||
      callEnded ||
      !callStarted ||
      callSessionIdRef.current !== sid
    ) {
      listeningRef.current = false;
      return;
    }
    if (listeningRef.current) return;
    listeningRef.current = true;
    setCallState("listening");
    let silenceTimeout: NodeJS.Timeout | null = null;
    let latestTranscript = "";
    let isSpeechFinal = false;

    try {
      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setCallState("thinking");
        throw new Error("Web Speech API not supported");
      }

      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      const SILENCE_DURATION = 30000;

      const resetSilenceTimer = () => {
        if (silenceTimeout) {
          clearTimeout(silenceTimeout);
        }
        silenceTimeout = setTimeout(() => {
          if (latestTranscript.trim() && !isSpeechFinal) {
            isSpeechFinal = true;
            recognition.stop();
          }
        }, SILENCE_DURATION);
      };

      recognition.onresult = (event: any) => {
        if (callSessionIdRef.current !== sid || callEnded) return; // Abort if session changed
        let interimTranscript = "";
        let finalTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
          } else {
            interimTranscript += transcript;
          }
        }

        latestTranscript = finalTranscript || interimTranscript;

        if (latestTranscript.trim()) {
          resetSilenceTimer();
        }

        if (finalTranscript) {
          isSpeechFinal = true;
          recognition.stop();
        }
      };

      recognition.onerror = (event: any) => {
        if (silenceTimeout) clearTimeout(silenceTimeout);
        recognitionRef.current = null;
        listeningRef.current = false;
        if (callSessionIdRef.current !== sid || callEnded) return;
        if (!callEnded && callStarted && micOn) {
          setCallState("listening");
          setTimeout(() => {
            if (
              micOn &&
              !callEnded &&
              callStarted &&
              callSessionIdRef.current === sid
            )
              listenAndRespond(sid);
          }, 100); // Slight delay to prevent rapid restarts
        } else {
          setCallState("connecting");
        }
      };

      recognition.onend = () => {
        if (silenceTimeout) clearTimeout(silenceTimeout);
        recognitionRef.current = null;
        listeningRef.current = false;

        if (callSessionIdRef.current !== sid || callEnded) return;
        if (latestTranscript.trim() && isSpeechFinal) {
          setCallState("thinking");
          // Only send threadId if it is defined
          sendToOpenAI(
            latestTranscript,
            undefined,
            threadId ? threadId : undefined
          )
            .then((aiResult) => {
              if (callSessionIdRef.current !== sid || callEnded) return;
              if (aiResult.threadId) {
                setThreadId(aiResult.threadId);
              }
              return playTTS(aiResult.response, sid);
            })
            .then(() => {
              if (callSessionIdRef.current !== sid || callEnded) return;
              setCallState("listening");
              if (
                callStarted &&
                !callEnded &&
                micOn &&
                callSessionIdRef.current === sid
              ) {
                setTimeout(() => {
                  if (
                    micOn &&
                    !callEnded &&
                    callStarted &&
                    callSessionIdRef.current === sid
                  )
                    listenAndRespond(sid);
                }, 100);
              }
            })
            .catch((e) => {
              if (callSessionIdRef.current !== sid || callEnded) return;
              console.error("Error in listenAndRespond:", e);
              setCallState("listening");
              if (
                callStarted &&
                !callEnded &&
                micOn &&
                callSessionIdRef.current === sid
              ) {
                setTimeout(() => {
                  if (
                    micOn &&
                    !callEnded &&
                    callStarted &&
                    callSessionIdRef.current === sid
                  )
                    listenAndRespond(sid);
                }, 100);
              }
            });
        } else {
          setCallState("listening");
          if (
            callStarted &&
            !callEnded &&
            micOn &&
            callSessionIdRef.current === sid
          ) {
            setTimeout(() => {
              if (
                micOn &&
                !callEnded &&
                callStarted &&
                callSessionIdRef.current === sid
              )
                listenAndRespond(sid);
            }, 100);
          }
        }
      };

      recognition.start();
      resetSilenceTimer();
    } catch (e) {
      if (callSessionIdRef.current !== sid || callEnded) return;
      console.error("Error in listenAndRespond:", e);
      if (silenceTimeout) clearTimeout(silenceTimeout);
      listeningRef.current = false;
      if (
        !callEnded &&
        callStarted &&
        micOn &&
        callSessionIdRef.current === sid
      ) {
        setCallState("listening");
        setTimeout(() => {
          if (
            micOn &&
            !callEnded &&
            callStarted &&
            callSessionIdRef.current === sid
          )
            listenAndRespond(sid);
        }, 100);
      } else {
        setCallState("connecting");
      }
    }
  }

  // Helper to concatenate Uint8Array chunks
  function concatUint8Arrays(chunks: Uint8Array[]): Uint8Array {
    let totalLength = 0;
    for (const chunk of chunks) totalLength += chunk.length;
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }

  // --- TTS playback logic ---
  async function playTTS(text: string, sessionId?: number) {
    const sid = sessionId ?? callSessionIdRef.current;
    stopTTS();
    setIsTTSPlaying(false);
    ttsPlayingRef.current = false;
    ttsAbortController.current = new AbortController();
    try {
      const stream = await elevenLabsTTSStream(
        text,
        ttsAbortController.current.signal
      );
      if (callSessionIdRef.current !== sid || callEnded) return;
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;
      const reader = stream.getReader();
      let source: AudioBufferSourceNode | null = null;
      let playing = true;
      let audioChunks: Uint8Array[] = [];

      await micMonitor.start();
      setCallState("talking"); // Always set to talking when TTS is playing
      setIsTTSPlaying(true);
      ttsPlayingRef.current = true;

      while (playing) {
        try {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) audioChunks.push(value);
          // If TTS was stopped/interrupted, break early
          if (
            !ttsPlayingRef.current ||
            callSessionIdRef.current !== sid ||
            callEnded
          ) {
            playing = false;
            break;
          }
        } catch (err: any) {
          // Suppress AbortError if TTS was intentionally stopped
          if (err?.name === "AbortError") {
            playing = false;
            break;
          }
          throw err;
        }
      }
      if (
        !ttsPlayingRef.current ||
        callSessionIdRef.current !== sid ||
        callEnded
      ) {
        micMonitor.stop();
        if (audioContext.state !== "closed") await audioContext.close();
        audioContextRef.current = null;
        return;
      }
      const audioData = concatUint8Arrays(audioChunks);
      const buffer = await audioContext.decodeAudioData(
        audioData.buffer.slice(
          audioData.byteOffset,
          audioData.byteOffset + audioData.byteLength
        ) as ArrayBuffer
      );
      if (callSessionIdRef.current !== sid || callEnded) {
        micMonitor.stop();
        if (audioContext.state !== "closed") await audioContext.close();
        audioContextRef.current = null;
        return;
      }
      source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      audioSourceRef.current = source;
      source.onended = () => {
        setIsTTSPlaying(false);
        ttsPlayingRef.current = false;
        micMonitor.stop();
        if (audioContext.state !== "closed") audioContext.close();
        audioContextRef.current = null;
        audioSourceRef.current = null;
      };
      source.start();
      await new Promise<void>((resolve) => {
        source!.onended = () => {
          setIsTTSPlaying(false);
          ttsPlayingRef.current = false;
          micMonitor.stop();
          if (audioContext.state !== "closed") audioContext.close();
          audioContextRef.current = null;
          audioSourceRef.current = null;
          resolve();
        };
      });
    } catch (e: any) {
      // Suppress AbortError if caused by stopTTS
      if (e?.name === "AbortError") {
        // Do not log or treat as error
        setIsTTSPlaying(false);
        ttsPlayingRef.current = false;
        micMonitor.stop();
        if (
          audioContextRef.current &&
          audioContextRef.current.state !== "closed"
        ) {
          audioContextRef.current.close();
        }
        audioContextRef.current = null;
        audioSourceRef.current = null;
        return;
      }
      console.error("Error in playTTS:", e);
      setIsTTSPlaying(false);
      ttsPlayingRef.current = false;
      micMonitor.stop();
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
      audioSourceRef.current = null;
    }
  }

  function stopTTS() {
    if (ttsAbortController.current) {
      ttsAbortController.current.abort();
      ttsAbortController.current = null;
    }
    ttsPlayingRef.current = false;
    setIsTTSPlaying(false);
    micMonitor.stop();
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
      } catch {}
      audioSourceRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== "closed") {
          audioContextRef.current.close();
        }
      } catch {}
      audioContextRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = ""; // Clear source to prevent lingering audio
    }
  }

  // --- Mic monitor: interrupt TTS if user speaks ---
  function handleUserSpeak() {
    // Only interrupt if enough time has passed since last interruption
    const now = Date.now();
    if (now - lastUserSpeakTime.current < 600) return; // Ignore if <600ms since last
    lastUserSpeakTime.current = now;

    if (ttsPlayingRef.current && !callEnded) {
      stopTTS();
      setCallState("listening");
      listeningRef.current = false;
      setTimeout(() => {
        if (!listeningRef.current && micOn && !callEnded && callStarted) {
          listenAndRespond();
        }
      }, 100);
    }
  }

  // --- Hang up logic ---
  const handleHangUp = () => {
    const newSessionId = callSessionIdRef.current + 1;
    setCallSessionId(newSessionId);
    callSessionIdRef.current = newSessionId; // update ref immediately
    setCallStarted(false);
    setMicOn(false);
    setCallEnded(true);
    stopTTS();
    micMonitor.stop();
    setCallState("connecting");

    // Stop microphone stream
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false; // Ensure track is disabled
      });
      micStreamRef.current = null;
    }

    // Stop recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        recognitionRef.current.abort?.();
      } catch (err) {
        console.error("Error stopping recognition in handleHangUp:", err);
      }
      recognitionRef.current = null;
    }
    listeningRef.current = false;

    // Pause and mute audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = "";
      audioRef.current.muted = true;
    }
  };

  // --- File upload handler ---
  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadedFile(e.target.files[0]);
    }
  };

  // --- For demo: manual state switcher ---
  const manualStateButtons = (
    <div className="flex gap-2 mt-2">
      <button
        onClick={() => setCallState("connecting")}
        className="px-2 py-1 border rounded text-xs"
      >
        Connecting
      </button>
      <button
        onClick={() => setCallState("listening")}
        className="px-2 py-1 border rounded text-xs"
      >
        Listening
      </button>
      <button
        onClick={() => setCallState("thinking")}
        className="px-2 py-1 border rounded text-xs"
      >
        Thinking
      </button>
      <button
        onClick={() => setCallState("talking")}
        className="px-2 py-1 border rounded text-xs"
      >
        Talking
      </button>
    </div>
  );

  // --- Restart listening if conditions are met ---
  useEffect(() => {
    if (
      callStarted &&
      !callEnded &&
      callState === "listening" &&
      !listeningRef.current &&
      micOn
    ) {
      listenAndRespond();
    }
  }, [callState, callStarted, callEnded, micOn]);

  // --- Ensure audio and mic cleanup on call end ---
  useEffect(() => {
    if (callEnded) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = "";
        audioRef.current.muted = true;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => {
          track.stop();
          track.enabled = false;
        });
        micStreamRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.abort?.();
        } catch (err) {
          console.error("Error stopping recognition on call end:", err);
        }
        recognitionRef.current = null;
      }
      listeningRef.current = false;
    }
  }, [callEnded]);

  // --- Mic toggle handler ---
  const handleMicToggle = () => {
    setMicOn((prev) => {
      const newMicOn = !prev;
      if (!newMicOn) {
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
            recognitionRef.current.abort?.();
          } catch (err) {
            console.error("Error stopping recognition in mic toggle:", err);
          }
          recognitionRef.current = null;
        }
        listeningRef.current = false;
      }
      if (
        newMicOn &&
        callStarted &&
        !callEnded &&
        callState === "listening" &&
        !listeningRef.current
      ) {
        setTimeout(() => {
          if (micOn && !callEnded && callStarted) listenAndRespond();
        }, 100);
      }
      return newMicOn;
    });
  };

  // Update callSessionId ref on change
  useEffect(() => {
    callSessionIdRef.current = callSessionId;
  }, [callSessionId]);

  return (
    <div className="relative h-screen flex flex-col font-inter">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 text-black flex items-center justify-between ${headerHeight} bg-white`}
      >
        <div className="profile-name-section ms-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/pitch-text")}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              marginRight: 12,
              outline: "none",
              boxShadow: "none",
              cursor: "pointer",
            }}
            tabIndex={0}
            aria-label="Back"
            className="focus:outline-none active:outline-none"
          >
            <img
              src="/chevron.png"
              alt="Back"
              style={{ width: 15, height: 15, display: "block" }}
              draggable={false}
            />
          </button>
          <span
            style={{
              display: "inline-block",
              width: 1,
              height: 28,
              background: "#bdbdbd",
              marginRight: 12,
            }}
          />
          <div className="relative">
            <img
              src="/fabrice-profile.jpg"
              alt="Fabrice Avatar"
              className="w-9 h-9 rounded-full object-cover border border-gray-200"
              style={{ background: "#f1f0ef" }}
            />
            <span
              className="absolute"
              style={{
                bottom: 2,
                right: 2,
                width: "12px",
                height: "12px",
                background: "#30a46c",
                borderRadius: "50%",
                border: "2px solid #fff",
                display: "block",
              }}
            />
          </div>
          <span>Pitch Fabrice</span>
        </div>
        <div className="menu-section"></div>
        <div className="profile-icons-section me-3 flex items-center gap-3">
          <img
            src="/fabric-favicon.png"
            alt="FG Logo"
            className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 object-contain"
            style={{ maxWidth: "2.5rem", maxHeight: "2.5rem" }}
          />
        </div>
      </header>
      {/* Body */}
      <main className="flex-1 overflow-y-auto pt-16 pb-28 flex flex-col items-center justify-center">
        <div className="flex flex-col items-center">
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: 230,
              height: 230,
              background: "#fff",
              border:
                callStarted && !callEnded && callState === "talking"
                  ? "none"
                  : "10px solid #e6e5e3",
              padding: 8,
              boxSizing: "border-box",
              marginBottom: 24,
              position: "relative",
            }}
          >
            {callStarted && !callEnded && callState === "talking" && (
              <span
                className="avatar-talking-highlight"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  background: "rgba(247, 107, 21, 0.25)",
                  filter: "blur(10px)",
                  zIndex: 0,
                  pointerEvents: "none",
                  animation: "avatar-talking-pulse 1.5s infinite",
                }}
              />
            )}
            {callStarted && !callEnded && callState === "listening" && (
              <span
                className="avatar-spinner"
                style={{
                  position: "absolute",
                  top: -10,
                  left: -10,
                  width: 230,
                  height: 230,
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              />
            )}
            <img
              src="/fabrice-profile.jpg"
              alt="Fabrice Avatar"
              className="w-full h-full rounded-full object-cover"
              style={{
                background: "#f1f0ef",
                position: "relative",
                zIndex: 1,
              }}
            />
          </div>
          {callStarted && !callEnded && (
            <div
              className="rounded-full flex items-center justify-center mb-6"
              style={{
                background:
                  callState === "thinking" || callState === "talking"
                    ? "#ffefd6"
                    : "#f1f0ef",
                minWidth: 180,
                minHeight: 40,
                padding: "0 28px",
                fontFamily: '"Poppins", sans-serif',
                fontSize: 15,
                fontWeight: 500,
                color:
                  callState === "thinking" || callState === "talking"
                    ? "#f76b15"
                    : "#222",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                transition: "opacity 0.2s, background 0.2s, color 0.2s",
              }}
            >
              {callState === "connecting" || callState === "listening" ? (
                <>
                  {callState.charAt(0).toUpperCase() + callState.slice(1)}
                  <span style={{ display: "inline-flex", marginLeft: 6 }}>
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        style={{
                          display: "inline-block",
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: dotIndex === i ? "#222" : "#c8c7c5",
                          margin: "0 2px",
                          transition:
                            "background 0.18s cubic-bezier(.4,2,.6,1)",
                        }}
                      />
                    ))}
                  </span>
                </>
              ) : callState === "thinking" ? (
                <>
                  Thinking
                  <span style={{ display: "inline-flex", marginLeft: 6 }}>
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        style={{
                          display: "inline-block",
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: "#f76b15",
                          opacity: dotIndex === i ? 1 : 0.3,
                          margin: "0 2px",
                          transition: "opacity 0.18s cubic-bezier(.4,2,.6,1)",
                        }}
                      />
                    ))}
                  </span>
                </>
              ) : callState === "talking" ? (
                <>
                  Talking
                  <span
                    style={{
                      display: "inline-flex",
                      marginLeft: 10,
                      alignItems: "center",
                      height: 35,
                    }}
                  >
                    <Waveform
                      size={20}
                      stroke={3.5}
                      speed={1}
                      color="#f76b15"
                    />
                  </span>
                </>
              ) : null}
            </div>
          )}
          <div
            className="text-4xl font-medium mb-4"
            style={{ fontFamily: '"Poppins", sans-serif' }}
          >
            Pitch Fabrice
          </div>
          {!callStarted && !callEnded ? (
            <button
              type="button"
              onClick={handleStartCall}
              className="bg-black text-white rounded-full px-5 py-3 text-md font-medium shadow transition"
              style={{ fontFamily: '"Poppins", sans-serif' }}
            >
              Start Call
            </button>
          ) : callStarted && !callEnded ? (
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-6 mt-2">
                <button
                  type="button"
                  onClick={handleMicToggle}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: micOn ? "#f1f0ef" : "#000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    outline: "none",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                  aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      micOn ? "#e0dfdd" : "#222";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      micOn ? "#f1f0ef" : "#000";
                  }}
                >
                  <img
                    src={micOn ? "/mic-on.png" : "/mic-off.png"}
                    alt={micOn ? "Mic On" : "Mic Off"}
                    style={{ width: 40, height: 40 }}
                    draggable={false}
                  />
                </button>
                <button
                  type="button"
                  onClick={handleHangUp}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "#feebec",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    outline: "none",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                  aria-label="Hang up"
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "#f9cfd2";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "#feebec";
                  }}
                >
                  <img
                    src="/hang-up.png"
                    alt="Hang Up"
                    style={{ width: 40, height: 40 }}
                    draggable={false}
                  />
                </button>
                <button
                  type="button"
                  onClick={handleFileUploadClick}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "#f1f0ef",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    outline: "none",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                  aria-label="Upload file"
                >
                  <img
                    src="/file-upload.png"
                    alt="Upload File"
                    style={{ width: 40, height: 40 }}
                    draggable={false}
                  />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
              {uploadedFile && (
                <div
                  className="rounded-full flex items-center justify-center mt-6"
                  style={{
                    background: "#f1f0ef",
                    minWidth: 180,
                    minHeight: 40,
                    padding: "0 28px",
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: 15,
                    fontWeight: 500,
                    color: "#222",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                    transition: "opacity 0.2s, background 0.2s, color 0.2s",
                    gap: 10,
                  }}
                >
                  <img
                    src="/attachment.png"
                    alt="Attachment"
                    style={{ width: 22, height: 22, marginRight: 8 }}
                    draggable={false}
                  />
                  <span
                    style={{
                      maxWidth: 180,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                    }}
                    title={uploadedFile.name}
                  >
                    {uploadedFile.name}
                  </span>
                </div>
              )}
            </div>
          ) : callEnded ? null : null}
        </div>
      </main>
      {callEnded && (
        <div
          className="fixed bottom-0 left-0 right-0 flex justify-center items-center pb-8"
          style={{
            zIndex: 40,
            background: "transparent",
            pointerEvents: "none",
          }}
        >
          <div
            className="flex items-center gap-6"
            style={{ pointerEvents: "auto" }}
          >
            <button
              type="button"
              onClick={() => navigate("/pitch-text")}
              style={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                background: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                outline: "none",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              aria-label="Go to chat"
            >
              <img
                src="/chat-bubble.png"
                alt="Chat"
                style={{ width: 25, height: 25 }}
                draggable={false}
              />
            </button>
            <button
              type="button"
              onClick={handleStartCall}
              style={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                background: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                outline: "none",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              aria-label="Start call"
            >
              <img
                src="/call-on.png"
                alt="Call On"
                style={{ width: 25, height: 25 }}
                draggable={false}
              />
            </button>
          </div>
        </div>
      )}
      <audio ref={audioRef} muted={!callStarted || callEnded} />
    </div>
  );
};

export default PitchFabriceVoiceView;
